import { useStore } from 'vuex'
import { computed, ref, onMounted } from 'vue'
import deepClone from 'foundation/js/deepClone'
import SearchResultCard from './subcomponents/SearchResultCard'
import RecipeCard from './subcomponents/RecipeCard'
import ActivityCard from '../ActivityCard/ActivityCard.vue'
import FrivilligjobCard from '../FrivilligjobCard/FrivilligjobCard.vue'
import LocationCard from '../LocationCard/LocationCard.vue'
import SortDropdown from '../SortDropdown/SortDropdown.vue'
import FundraisingCard from '../FundraisingCard/FundraisingCard.vue'
import DonationCard from '../DonationCard/DonationCard.vue'
import SimpleCard from '../SimpleCard/SimpleCard.vue'
import SpotCard from '../SpotCard/SpotCard.vue'
import GalleryCard from '../GalleryCard/GalleryCard.vue'
import SpotCardGrid from '../SpotCardGrid/SpotCardGrid.vue'
import eventDataAdapter from './subcomponents/eventDataAdapter'

const sortOptions = {
    fundraising : {
        "name":"Sorter",
        "options": [
            {
                "name":"Senest oprettet",
                "handler": "latest"
            },
            {
                "name":"Flest indsamlet penge",
                "handler": "highestCollected"
    
            }
        ]
    }
}

const urlMap = {
    counselling: 'COUNSELLING_SEARCH',
    events: 'EVENT_SEARCH',
    fundraising: 'FUNDRAISING_SEARCH',
    donations: 'DONATION_SEARCH',
    gallery: 'GALLERY_SEARCH',
    patientAssociations: 'PATIENT_ASSOCIATIONS_SEARCH',
    jobs: 'FRIVILLIGJOB_SEARCH',
    cancerforum: 'CANCERFORUM_SEARCH',
}

const getUrl = function(type){
    return urlMap[type] ?? 'GENERAL_SEARCH'
}

const generalSearch = function(props,store){
    store.dispatch('search/initSearchScope',{
        guid:props.guid,
        firstPageSize:props.firstPageSize,
        showMorePageSize:props.showMorePageSize,
        url: process.env[getUrl(props.type)]
    })

    const inputText = new URLSearchParams(document.location.search).get('q')
    const skip = new URLSearchParams(document.location.search).get('skip')
    const take = new URLSearchParams(document.location.search).get('take') ?? props.take
    store.dispatch('search/performZeroSearch',{
        guid:props.guid
    })
    
    store.dispatch('search/performSearch',{
        guid:props.guid,
        query:inputText,
        skip,
        take
    })
}

export default {
    name: "GeneralSearch",
    components: {SearchResultCard,RecipeCard,ActivityCard, FrivilligjobCard, LocationCard, FundraisingCard,SortDropdown, DonationCard, GalleryCard, SimpleCard, SpotCard, SpotCardGrid},
    props:{
        guid: {
            type: String,
            required:true
        },
        type: {
            type: String,
            required:false,
            default: "general"
        },
        firstPageSize: {
            type: Number,
            required:false
        },
        showMorePageSize: {
            type: Number,
            required:false
        },
        spotCardData: {
            type: Object,
            required:false
        },
        hideNoResults: {
            type: Boolean,
            required:false,
            default:false
        },
        avoidSetQueryInUrl:
        {
            type: Boolean,
            required:false,
            default: false
        },
        isMultisearch:
        {
            type: Boolean,
            required:false,
            default: false
        }
    },
    setup(props) {
        const inputText = computed(()=>store.state.search.queries[props.guid] ?? "")
        const store = useStore()
        const results = computed(()=> props.type === 'events' 
            ? eventDataAdapter(deepClone(store.state.search.results[props.guid] ?? [])) 
            : deepClone(store.state.search.results[props.guid] ?? []))
        const totalCount = computed(()=> store.state.search.scoped_total[props.guid] ?? 0)
        const tags = computed(()=> store.getters['search/selectedTags'](props.guid) ?? [])        
        const showSpinner = computed(()=> store.state.search.is_searching)
        const searchLabelCollection = props.isMultisearch ? `generalSearch-${props.guid}` : "generalSearch"
        const getLabel = (labelKey)=> store.getters['labels/getLabel']({labelCollection: searchLabelCollection, labelKey: labelKey})
        const existLabel = (labelKey)=> store.getters['labels/existLabel']({labelCollection: searchLabelCollection, labelKey: labelKey})

        const getMoreResults = function(){
            store.dispatch('search/performMoreSearch',{
                guid:props.guid
            })
        }

        onMounted(()=>{
            if (props.avoidSetQueryInUrl) {
                store.commit('search/set_replaceQueryInUrl', false)
            }

            if(props.guid === 'dictionarySearch'){
                store.dispatch('search/initSearchScope',{
                    guid:props.guid,
                    url: null,
                })
            } else {
                generalSearch(props,store)
            }
        })

        const getResultImageRatio = function(result) {
            if (!result || !result.documentType) return null

            return {
                'NewsPage': '3-2',
                'LongreadPage': '3-2',
                'PodcastPage': '1-1',
                'RecipePage': '4-5',
                'ReportPage': 'A4'
            }[result.documentType] ?? null
        }

        const getResultImageType = function(result) {
            if (!result || !result.documentType) return null

            return {
                'PodcastPage': 'podcast',
                'RecipePage': 'recipe',
                'ReportPage': 'report'
            }[result.documentType] ?? null
        }

        const clearFilterOnTag = function(tag) {
            store.dispatch('search/performSearch',{
                guid:props.guid,
                query: inputText.value,
                facet: tag
            })
        }

        const sortingHandler = function(sorting){
            store.dispatch('search/performSearch',{
                guid:props.guid,
                sorting
            })
        }

        const canShowMore = computed(()=>store.getters['search/canShowMore'](props.guid))

        // if list is type with grid, class placed on parent in order to hit meta-top-container too
        const gridTypeList = () => {
            // recipe grid has unique 2 col mobile grid, hence modifier
            if (props.type === 'recipe' ) {
                return 'kb-grid-list kb-grid-list--recipe'
            } else if (props.type ===  'counselling' || 'fundraising' ) {
                return 'kb-grid-list'
            } 
        }

        const displayProperties = {
            'news': {card: 'SearchResultCard', grid: false, date: true, showCardTitle: true, summary: {hidable: true}, image: true, breadcrumbs: false},
            'recipe': {card: 'RecipeCard', grid: true, listType:'4col'},
            'events': {card: 'ActivityCard', grid: false, listType: 'fixed-header'},
            'donations': {card: 'DonationCard', grid: false, hideTotalCount: true, listType:'content-width'},
            'gallery': {card: 'GalleryCard', grid: false, hideTotalCount: false},
            'fundraising': {card: 'FundraisingCard', grid: true, listType:'3col', sortOptions:sortOptions['fundraising']},
            'counselling': {card: 'LocationCard', grid: true, hideTotalCount: true, hideTags: true, showSearchTitleCount: true, listType:'3col'},
            'patientAssociations': {card: 'SimpleCard', grid: true, listType:'3col'},
            'documentList': {card: 'SimpleCard', grid: true, listType:'3col'},
            'jobs': {card: 'FrivilligjobCard', grid: false,  hideTotalCount: false, hideTags: false, showSearchTitleCount: true, showCtaButton: true },

        }[props.type] ?? {card: 'SearchResultCard', grid: false, date: false, showCardTitle: true, summary: true,  image: true, breadcrumbs: true}

        const displayModifications = computed (() => {
            
            const grid = 'kb-general-search__results--grid'
            const list = 'kb-general-search__results--list'
            const gridModifications = (displayProperties.grid && displayProperties.listType) ? displayProperties.listType ? grid + '-' + displayProperties.listType : '' : null
            const listModifications = (!displayProperties.grid && displayProperties.listType) ? list + '-' + displayProperties.listType : null

            return gridModifications
                ? gridModifications
                : listModifications
                    ? listModifications
                    : ''
        })

        // Flatten the results in case the list is fixed-header, where the results are grouped in categories.
        // This was we can make sure to place the spot card in the correct position
        const flatResults = computed(() => {
            if (displayProperties.listType === 'fixed-header') {
                return Object.values(results.value).flatMap(month => month.entries)
            }

            return results.value
        })

        const resultCount = computed(()=>{
            if (displayProperties.listType === 'fixed-header') {
                let totalCount = 0
                
                for (const key in results.value) {
                    if (results.value.hasOwnProperty(key)) {
                        const array = results.value[key].entries
                        totalCount += array.length
                    }
                }

                return totalCount
            }

            return results.value.length
        })

        const totalCountLabel = computed(() => getLabel('resultCountTitle').replace('{}', totalCount.value))
        const searchTitleCountLabel = computed(() => getLabel('searchTitleCount')?.replace('{}', totalCount.value))

        const spotCard = displayProperties.grid ? SpotCardGrid : SpotCard
        const spotCardIndex = computed(()=> Math.min(props.spotCardData.position - 1, totalCount.value))
    
        const shouldRenderSpotCard = function(result, isLastRender) {
            if (!props.spotCardData) return false

            const realIndex = flatResults.value.findIndex(r => r === result)

            if (!isLastRender) {
                return realIndex === spotCardIndex.value
            }

            return (realIndex === resultCount.value - 1) && (spotCardIndex.value >= resultCount.value)
        }

        return {
            displayProperties,
            results,
            resultCount,
            tags,
            getLabel,
            getMoreResults,
            clearFilterOnTag,
            gridTypeList,
            showSpinner,
            totalCount,
            totalCountLabel,
            searchTitleCountLabel,
            canShowMore,
            sortingHandler,
            displayModifications,
            spotCard,
            spotCardIndex,
            shouldRenderSpotCard,
            canShowMore,
            getResultImageRatio,
            getResultImageType,
            existLabel
        }
    }
}
